import { useState } from "react";
import { format } from "date-fns";
import { Loader2, X, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import apiClient from "./api";
import { SERVICES } from "../data/mock";

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

export default function ReminderModal({ open, onClose, onCreated, defaults }) {
  const [form, setForm] = useState({
    customer_name: defaults?.customer_name || "",
    customer_phone: defaults?.customer_phone || "",
    customer_email: defaults?.customer_email || "",
    service: defaults?.service || "",
    pet_name: defaults?.pet_name || "",
    pet_breed: defaults?.pet_breed || "",
    notes: defaults?.notes || "",
    lead_source: defaults?.lead_source || "",
  });
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !date) {
      toast.error("Customer name and reminder date are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        remind_on: format(date, "yyyy-MM-dd"),
      };
      // Strip empty optional fields
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      // customer_name is required
      payload.customer_name = form.customer_name;
      payload.remind_on = format(date, "yyyy-MM-dd");
      const { data } = await apiClient.post("/api/admin/reminders", payload);
      toast.success("Reminder set");
      onCreated && onCreated(data);
      onClose();
    } catch {
      toast.error("Could not save reminder");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reminder-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#C9BCFF]">New reminder</p>
            <h2 className="font-heading text-lg font-bold text-white">Add customer follow-up</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="reminder-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="reminder-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Customer name *</label>
              <input
                data-testid="reminder-customer-name"
                value={form.customer_name}
                onChange={(e) => update("customer_name", e.target.value)}
                placeholder="e.g. Ananya Mishra"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Phone</label>
              <input
                data-testid="reminder-customer-phone"
                value={form.customer_phone}
                onChange={(e) => update("customer_phone", e.target.value)}
                placeholder="98XXXXXXXX"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Email</label>
              <input
                data-testid="reminder-customer-email"
                type="email"
                value={form.customer_email}
                onChange={(e) => update("customer_email", e.target.value)}
                placeholder="optional"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Pet name</label>
              <input
                data-testid="reminder-pet-name"
                value={form.pet_name}
                onChange={(e) => update("pet_name", e.target.value)}
                placeholder="e.g. Coco"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Breed</label>
              <input
                data-testid="reminder-pet-breed"
                value={form.pet_breed}
                onChange={(e) => update("pet_breed", e.target.value)}
                placeholder="e.g. Labrador"
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Lead source</label>
              <Select value={form.lead_source} onValueChange={(v) => update("lead_source", v)}>
                <SelectTrigger data-testid="reminder-lead-source" className={inputCls + " text-left"}>
                  <SelectValue placeholder="Where did this lead come from?" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60 z-[90]">
                  <SelectItem value="Website">Website</SelectItem>
                  <SelectItem value="Direct Call">Direct Call</SelectItem>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Walk-in">Walk-in</SelectItem>
                  <SelectItem value="Social Media">Social Media (Instagram / Facebook)</SelectItem>
                  <SelectItem value="Activation Programme">Activation Programme</SelectItem>
                  <SelectItem value="Referral">Referral (word of mouth)</SelectItem>
                  <SelectItem value="Google Maps">Google Maps / Search</SelectItem>
                  <SelectItem value="Justdial">Justdial</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Service interested in</label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="reminder-service-select" className={inputCls + " text-left"}>
                  <SelectValue placeholder="Pick a service (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60 z-[90]">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Remind me on *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="reminder-date-picker"
                    className={`${inputCls} text-left flex items-center justify-between`}
                  >
                    <span className={date ? "text-white" : "text-white/40"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalIcon className="w-4 h-4 text-[#C9BCFF]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10 z-[90]" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Notes</label>
              <textarea
                data-testid="reminder-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Why should we remind ourselves about this customer?"
                className={inputCls + " resize-none"}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="reminder-submit"
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : "Save reminder"}
          </button>
        </form>
      </div>
    </div>
  );
}
